import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarClock, Settings, LogOut, ExternalLink, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import Footer from "./Footer";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import SubscriptionExpired from "./SubscriptionExpired";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/dashboard/appointments", label: "Citas", icon: CalendarClock, testId: "nav-appointments" },
  { to: "/dashboard/settings", label: "Configuración", icon: Settings, testId: "nav-settings" },
];

export default function OwnerLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);

  useEffect(() => {
    api.get("/business/me").then(({ data }) => setBusiness(data)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (business && business.active === false) {
    return <SubscriptionExpired business={business} />;
  }

  return (
    <div className="min-h-screen bg-void text-neutral-100 flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 lg:min-h-screen lg:border-r lg:border-gold/10 lg:sticky lg:top-0 bg-void">
        <div className="p-5 border-b border-gold/10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold to-neonpink flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-void" strokeWidth={2} />
          </div>
          <div>
            <div className="font-display text-xl leading-none">AgendaPro</div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-neutral-500 mt-1">
              {user?.name || "Owner"}
            </div>
          </div>
        </div>

        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto hide-scrollbar">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/dashboard"}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                  isActive
                    ? "bg-surfaceHi text-gold border border-gold/30"
                    : "text-neutral-400 hover:text-neutral-100 hover:bg-surface"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 lg:absolute lg:bottom-0 lg:w-64 border-t border-gold/10 lg:border-t hidden lg:block">
          {business?.slug && (
            <a
              href={`/agenda/${business.slug}`}
              target="_blank"
              rel="noreferrer"
              data-testid="public-page-link"
              className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest text-cyan hover:text-neonpink transition-colors duration-200"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Ver página pública
            </a>
          )}
          <Button
            variant="ghost"
            className="w-full justify-start text-neutral-400 hover:text-neonpink"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <div className="flex-1 p-4 md:p-8 max-w-7xl w-full mx-auto">{children}</div>
        <Footer />
      </main>
    </div>
  );
}
